
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import CanonicalTag from "@/components/CanonicalTag";

const ADUCostAtherton = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "UDA ADU",
    
    "url": "https://www.udaadu.com/adu-cost-atherton",
    "logo": "https://www.udaadu.com/logo.png",
    "image": "https://www.udaadu.com/logo.png",
    "description": "UDA ADU specializes in high-quality, transparent ADU cost planning and construction services in Atherton.",
    "email": "info@udaadu.com",
    "telephone": "+1-408-912-8989",
    "areaServed": {
      "@type": "Place",
      "name": "Atherton"
    },
    "founder": {
      "@type": "Person",
      "name": "Mona Poudat"
    },
    "foundingDate": "2014-01-28",
    "legalName": "UDA ADU",
    "sameAs": [
      "https://www.instagram.com/uda__adu/"
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/adu-cost-atherton" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        <section className="text-center py-16">
          {/* Hero Section */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light mb-5 text-white">
            How Much Does It Cost to Build an ADU in Atherton?
          </h1>

          <p className="text-lg md:text-xl leading-relaxed max-w-4xl mx-auto text-white mb-8">
            The cost of building an Accessory Dwelling Unit (ADU) in Atherton varies based on design, permitting, and construction choices. UDA ADU provides accurate, location-based estimates with our design-build expertise and transparent pricing model.
          </p>

          <div className="mb-8">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white text-lg py-4 px-8 rounded-xl shadow-lg transition-colors font-normal">
                Get an Instant ADU Cost Estimate →
              </Button>
            </Link>
          </div>

          {/* Video Section */}
          <div className="mb-10">
            <video 
              autoPlay 
              muted 
              loop 
              playsInline 
              disablePictureInPicture 
              onContextMenu={() => false} 
              controlsList="nodownload nofullscreen noremoteplay" 
              className="w-full max-w-4xl mx-auto rounded-3xl shadow-2xl"
              style={{ pointerEvents: 'none' }}
            >
              <source 
                src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/uda-santa-clara-model-adu-video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvdWRhLXNhbnRhLWNsYXJhLW1vZGVsLWFkdS12aWRlby5tcDQiLCJpYXQiOjE3NDk4MjM0NjYsImV4cCI6MjA2NTE4MzQ2Nn0.6j9a3WgKBNaWvqzI8BYfSDDzkmYFno9qllvOCEmBKWc" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Image Gallery */}
          <div className="flex flex-wrap justify-center gap-5 mb-10">
            <img 
              src="/lovable-uploads/7d021e95-bfde-4023-8266-6c8479134814.png" 
              alt="Modern backyard ADU" 
              className="w-72 rounded-xl shadow-lg"
            />
            <img 
              src="/lovable-uploads/99cde6f3-76e0-48da-8ece-20b8837d348b.png" 
              alt="Contemporary ADU white box" 
              className="w-72 rounded-xl shadow-lg"
            />
            <img 
              src="/lovable-uploads/af091130-a7f0-4012-ae37-c1a6eb99917b.png" 
              alt="Modern craftsman-style ADU" 
              className="w-72 rounded-xl shadow-lg"
            />
          </div>

          {/* CTA Section */}
          <div className="mb-10">
            <Button 
              onClick={openCalendlyModal}
              className="bg-[#FF7A00] hover:bg-[#e56b00] text-white text-lg py-4 px-8 rounded-xl shadow-lg transition-colors font-normal"
            >
              Book Your Complimentary ADU Consultation
            </Button>
          </div>

          {/* Footer Link */}
          <div className="mt-10">
            <p className="text-white text-base">
              Want to learn more? Visit our{" "}
              <Link to="/faq" className="text-[#FF7A00] underline hover:no-underline">
                Frequently Asked Questions
              </Link>
              {" "}page.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ADUCostAtherton;
